import * as React from "react"
import dayjs from "dayjs"
import { useMutation } from "react-query"
import { BiEdit, BiX, BiCheck } from "react-icons/bi"
import { FiTrash } from "react-icons/fi"

import {
  Flex,
  Text,
  IconButton,
  Button,
  Input,
  Box,
  Center,
  Stack,
  HStack,
  InputGroup,
  InputLeftAddon,
  Avatar,
  Tooltip,
} from "@chakra-ui/react"

import { ZoneEntity } from "../lib/types/zone"
import { fetcher } from "../lib/fetcher"
import { useToast } from "../lib/hooks/useToast"
import { Bbox, createBboxFromBounds, getZoneBounds, MultiPolygonData } from "../lib/helpers/map"
import { OrgEntity } from "../lib/types/org"
import { useOrgs } from "./providers/OrgProvider"
import { Confirmation } from "./Confirmation"

interface Props {
  index: number
  zone: ZoneEntity
  isSelected: boolean
  handleSelectZone: () => void
  polygonCoords?: MultiPolygonData
  isCreating: boolean
  editableZone: ZoneEntity | null
  onSetBbox: (bbox: Bbox) => void
  refetch: () => void
  organization: OrgEntity
  setEditableZone: (zone: ZoneEntity | null) => void
}

interface UpdateRequest {
  name: string | null
  high_threshold?: number
  low_threshold?: number
  polygon?: {
    coordinates?: MultiPolygonData
    type: string
  }
}

export const ZoneItem = React.forwardRef(function _ZoneItem(props: Props, ref: any) {
  const toast = useToast()
  const { currentOrgs } = useOrgs()
  const [name, setName] = React.useState<string>(props.zone.name)
  const [high, setHigh] = React.useState<number | undefined>(props.zone.high_threshold)
  const [low, setLow] = React.useState<number | undefined>(props.zone.low_threshold)

  const { mutateAsync: destroy, isLoading: isDeleteLoading } = useMutation<undefined, unknown>(() =>
    fetcher(`zones/${props.zone.uuid}/`, { method: "delete" }),
  )

  const { mutateAsync: update, isLoading: isUpdateLoading } = useMutation<ZoneEntity, unknown, UpdateRequest>(
    (data) => fetcher(`zones/${props.zone.uuid}/`, { body: JSON.stringify(data), method: "PATCH" }),
  )

  const handleUpdateZone = async () => {
    if (!name) return
    try {
      if (!props.polygonCoords || props.polygonCoords[0].length === 0)
        return toast({ status: "error", description: "Please create a valid zone" })
      const res = await update({
        name,
        high_threshold: high,
        low_threshold: low,
        polygon: { coordinates: props.polygonCoords, type: "MultiPolygon" },
      })
      if (res.detail?.non_field_errors) {
        toast({ title: res?.detail?.non_field_errors[0].message, status: "error" })
      } else if (res?.uuid) {
        props.setEditableZone(null)
        props.refetch()
        toast({ title: "Successfully updated zone", status: "success" })
      } else {
        toast({ title: "Error updating zone", status: "error" })
      }
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const handleDeleteZone = async () => {
    try {
      const res = await destroy()
      if (!res) {
        props.refetch()
        toast({ title: "Successfully deleted zone", status: "success" })
      } else {
        toast({ title: "Error deleting zone", status: "error" })
      }
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const handleZoneClick = () => {
    props.handleSelectZone()
    const bounds = getZoneBounds(props.zone)
    if (bounds) {
      props.onSetBbox(createBboxFromBounds(bounds))
    }
  }

  return (
    <div ref={ref}>
      <Flex
        w="100%"
        align="center"
        justify="space-between"
        bg={props.index % 2 === 0 ? "gray.50" : "white"}
        border="2px solid"
        borderColor={props.isSelected ? "green.500" : "transparent"}
        p={3}
        mb={2}
        borderRadius="lg"
      >
        <Box w="100%">
          {props.editableZone?.uuid === props.zone.uuid ? (
            <Stack spacing={2}>
              <Input
                autoFocus
                isRequired
                defaultValue={props.zone.name}
                onChange={(e) => setName(e.target.value)}
              />
              {props.zone.type === "dropzone" && (
                <HStack>
                  <InputGroup size="sm">
                    <InputLeftAddon>Low</InputLeftAddon>
                    <Input
                      autoFocus
                      isRequired
                      defaultValue={props.zone.low_threshold}
                      onChange={(e) => setLow(parseInt(e.target.value))}
                      placeholder="limit"
                    />
                  </InputGroup>
                  <InputGroup size="sm">
                    <InputLeftAddon>High</InputLeftAddon>
                    <Input
                      autoFocus
                      isRequired
                      defaultValue={props.zone.high_threshold}
                      onChange={(e) => setHigh(parseInt(e.target.value))}
                      placeholder="limit"
                    />
                  </InputGroup>
                </HStack>
              )}
            </Stack>
          ) : (
            <Flex align="center">
              {currentOrgs.length > 1 && (
                <Box mr={2}>
                  <Tooltip label={props.organization?.name}>
                    <Avatar name={props.organization?.name} src={props.organization?.icon} size="xs" />
                  </Tooltip>
                </Box>
              )}
              <Box>
                <Button
                  variant="link"
                  justifyContent="flex-start"
                  onClick={handleZoneClick}
                  fontSize="1rem"
                  fontWeight="bold"
                  color="gray.700"
                >
                  {props.zone.name}
                </Button>

                {props.zone.type === "dropzone" && (
                  <Stack fontSize="0.7rem" color="gray.500" isInline>
                    {props.zone.low_threshold && <Text>Low threshold: {props.zone.low_threshold}</Text>}
                    {props.zone.high_threshold && <Text>High threshold: {props.zone.high_threshold}</Text>}
                  </Stack>
                )}

                <Text fontSize="0.7rem" color="gray.500">
                  Created: {dayjs(props.zone.modified).format("MMM DD, YYYY")}
                </Text>
              </Box>
            </Flex>
          )}
        </Box>
        <Center>
          {props.editableZone?.uuid === props.zone.uuid ? (
            <Flex>
              <IconButton
                size="sm"
                ml={1}
                variant="ghost"
                colorScheme="green"
                aria-label="Confirm edit zone"
                icon={<Box as={BiCheck} boxSize="20px" />}
                isLoading={isUpdateLoading}
                onClick={handleUpdateZone}
              />
              <IconButton
                size="sm"
                ml={1}
                variant="ghost"
                colorScheme="red"
                aria-label="Cancel edit zone"
                icon={<Box as={BiX} boxSize="20px" />}
                onClick={() => props.setEditableZone(null)}
              />
            </Flex>
          ) : (
            <>
              <IconButton
                size="sm"
                ml={1}
                variant="ghost"
                aria-label="Edit zone"
                icon={<BiEdit size="15px" />}
                onClick={() => props.setEditableZone(props.zone)}
                isDisabled={props.isCreating}
              />
              <Confirmation
                placement="right"
                header="Delete Zone"
                text="Are you sure you want to delete this zone?"
                onSubmit={handleDeleteZone}
              >
                <IconButton
                  size="sm"
                  ml={1}
                  variant="ghost"
                  colorScheme="red"
                  aria-label="Delete zone"
                  icon={<Box as={FiTrash} boxSize="15px" />}
                  isDisabled={props.isCreating}
                  isLoading={isDeleteLoading}
                />
              </Confirmation>
            </>
          )}
        </Center>
      </Flex>
    </div>
  )
})
